import {COMPANY_DOCUMENT_TYPES, getDocumentMaxFiles} from '@sollapay/domain';

import type {DocumentUploadTab} from '../components';
import type {DocumentType} from '@sollapay/enums';
import type {DocumentDto} from '@sollapay/types';
import type {TFunction} from 'i18next';

/**
 * Returns the translated label for a document type.
 * Always use this instead of calling t('common:documentTypes.X') directly,
 * so all document type labels (upload tabs, badges, tables) stay in sync.
 */
export function getDocumentTypeLabel(type: DocumentType, t: TFunction): string {
  return t(`common:documentTypes.${type}`);
}

export function buildDocumentTab(documentType: DocumentType, t: TFunction): DocumentUploadTab {
  return {
    value: documentType,
    label: getDocumentTypeLabel(documentType, t),
    documentType,
    maxFiles: getDocumentMaxFiles(documentType)
  };
}

export function buildCompanyDocumentTabs(t: TFunction): DocumentUploadTab[] {
  return COMPANY_DOCUMENT_TYPES.map(documentType => buildDocumentTab(documentType, t));
}

/**
 * Error string for a required-document upload slot whose type is still missing — shown only once
 * the surrounding modal is displaying errors live. Shared by every activation step that gates on
 * document presence via `useDocumentRequiredForm`, so the copy and the `showErrors` gating stay
 * identical everywhere it's used.
 */
export function resolveDocumentError(
  documentType: DocumentType,
  missingDocumentTypes: ReadonlySet<DocumentType>,
  showErrors: boolean,
  t: TFunction
): string | undefined {
  return showErrors && missingDocumentTypes.has(documentType)
    ? t('common:documents.requiredDocument')
    : undefined;
}

/** Opens the storage URL itself: a `blob:` URL would run an uploaded PDF's script in the app origin, beside the auth tokens. */
export async function openDocumentPreview(url: string): Promise<void> {
  // Probe first so a missing object (NoSuchKey XML) becomes an error, not a raw page.
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
  }
  await response.body?.cancel();
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Whether a document's metadata matches every key in `metadataFilter`. A filter value of
 * `undefined` requires the key to be absent from the document's metadata.
 */
export function matchesMetadataFilter(
  document: Pick<DocumentDto, 'metadata'>,
  metadataFilter?: Record<string, string | undefined>
): boolean {
  if (!metadataFilter) return true;
  const docMeta = (document.metadata as Record<string, unknown> | null) ?? {};
  return Object.entries(metadataFilter).every(([key, value]) =>
    value === undefined ? docMeta[key] === undefined : docMeta[key] === value
  );
}

/**
 * Returns the most recently uploaded document of a given type, or undefined.
 *
 * When `metadataFilter` is provided, only documents matching it are considered — needed
 * when an entity's document namespace is shared across separate sessions/purchases (e.g.
 * a trust account's buyer-add flow), so a leftover document from another session isn't
 * mistaken for the current one's.
 */
export function getLatestDocument(
  documents: DocumentDto[] | undefined,
  documentType: DocumentType,
  metadataFilter?: Record<string, string | undefined>
): DocumentDto | undefined {
  return documents
    ?.filter(d => d.documentType === documentType && matchesMetadataFilter(d, metadataFilter))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .at(0);
}
